import React from "react";
import { motion } from "framer-motion";
import { BookOpen, Utensils, Gift, Mail, Plane } from "lucide-react";
import { Chapter, MaskLines, Reveal } from "./Reveal";
import { HeartIcon } from "./Deco";

const ICONS = { book: BookOpen, utensils: Utensils, gift: Gift, mail: Mail };

const listV = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.1 } },
};
const itemV = {
  hidden: { opacity: 0, y: 34 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};

export const Program = ({ t }) => {
  return (
    <section id="program" className="relative paper py-24 sm:py-32 px-5 overflow-hidden">
      <div className="max-w-2xl mx-auto">
        <div className="text-center">
          <Chapter>{t.chapter}</Chapter>
          <MaskLines lines={[t.title]} className="font-serif text-4xl sm:text-6xl text-[var(--forest)]" />
          <Reveal delay={0.12}>
            <p className="font-script text-3xl sm:text-4xl text-[var(--gold-dark)] mt-1 flex items-center justify-center gap-2">
              Günün Akışı <Plane size={18} className="rotate-45" />
            </p>
          </Reveal>
        </div>

        <motion.div
          className="mt-16 relative"
          variants={listV}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-90px" }}
        >
          {/* animated flight line */}
          <motion.span
            className="absolute left-8 top-3 bottom-3 w-[2px] rounded-full bg-gradient-to-b from-[var(--gold)] via-[var(--gold-dark)] to-[var(--gold)]/20 origin-top"
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.3, ease: "easeInOut" }}
          />
          {/* departure plane at top of line */}
          <motion.div
            className="absolute left-[22px] -top-4 text-[var(--gold-dark)]"
            initial={{ opacity: 0, y: -8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <Plane size={22} className="-rotate-45" />
          </motion.div>

          {t.items.map((item, i) => {
            const Icon = ICONS[item.icon] || Mail;
            return (
              <motion.div
                key={i}
                variants={itemV}
                className="relative flex gap-4 sm:gap-6 mb-7 sm:mb-9 last:mb-0 group"
              >
                {/* stop dot */}
                <div className="relative z-10 shrink-0 w-16 flex justify-center">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full leather flex items-center justify-center shadow-lg ring-1 ring-[var(--gold)]/40 transition-transform duration-300 group-hover:scale-105">
                    <Icon className="text-[var(--gold)]" size={24} />
                  </div>
                </div>

                {/* card */}
                <div className="flex-1 rounded-2xl border border-[var(--gold-dark)]/20 bg-white/45 backdrop-blur-sm p-4 sm:p-5 shadow-[0_4px_18px_rgba(138,109,47,0.08)] transition-all duration-300 group-hover:-translate-y-0.5 group-hover:shadow-[0_12px_30px_rgba(138,109,47,0.18)] group-hover:border-[var(--gold-dark)]/40">
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 rounded-full bg-[var(--forest)] text-[var(--gold-light)] font-mono text-sm tracking-[0.12em] tabular-nums shrink-0">
                      {item.time}
                    </span>
                    <span className="h-px flex-1 bg-[var(--gold-dark)]/20" />
                  </div>
                  <p className="font-serif text-xl sm:text-2xl text-[var(--forest)] mt-3 leading-snug">{item.tr}</p>
                  {item.en && item.en !== item.tr && (
                    <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-[var(--gold-dark)]/70 mt-1.5">
                      {item.en}
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })}

          {/* arrival heart at bottom of line */}
          <motion.div
            className="absolute left-[18px] -bottom-2 w-7 h-7 rounded-full bg-gradient-to-r from-[#BF953F] to-[#B38728] flex items-center justify-center shadow-md"
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.9, type: "spring", stiffness: 200, damping: 12 }}
          >
            <HeartIcon size={12} className="text-[var(--forest)]" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
